import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '~/apis/cart';
import QuantityStepMixin from "~/mixins/QuantityStepMixin";
import { getFromServer } from "~/composables/getFromServer";
import ProductAddedToCart from "~/components/Modals/ProductAddedToCart.vue";
import { useModal } from "~/stores/ModalStore";
import {useCheckoutStore} from "~/stores/CheckoutStore.js";

export const useCartStore = defineStore('cart', () => {
    const items = ref([]);
    const products = ref([]);
    const productsDirty = ref(true);
    const withVat = ref(false);
    const anonymous = ref(false);
    const count = computed(() =>
        items.value.reduce((qty, item) => qty + item.quantity, 0)
    );
    const defaultKey = useCookie('cart-key', () => 'default');
    const key = ref(defaultKey.value || 'default');

    const pendingRemovalIds = ref([]);
    const checkoutErrors = ref([]);
    let removalTimeout = null;

    function initData(data) {
        items.value = data;
    }

    async function setKey(newKey) {
        if (!newKey || newKey === key.value) {
            return;
        }
        key.value = newKey || defaultKey.value;
        defaultKey.value = key.value;
        await fetchCart(true);
    }

    async function fetchCart(force, empty) {
        if (!force && items.value.length > 0) {
            return;
        }
        items.value = await getFromServer('cart?cart-key=' + key.value + (empty ? '&empty=1' : ''));
        productsDirty.value = true;
        useCheckoutStore().refreshCheckoutKey();
    }

    function addToCart(product_id, quantity = 1, silent = false) {
        storeUpdate(product_id, quantity);

        return api.storeUpdate(product_id, quantity, key.value, silent)
            .then(markProductsAsDirty);
    }

    function addToCartSilent(product_id, quantity = 1) {
        storeUpdate(product_id, quantity);

        return api.storeUpdate(product_id, quantity, key.value, true)
            .then(markProductsAsDirty);
    }

    async function updateCart(id, quantity, silent = true) {
        if (silent) {
            storeUpdate(id, quantity);
        } else {
            storeUpdate(id, quantity + getProductQuantity(id));
        }

        return api.storeUpdate(id, getProductQuantity(id), key.value, silent)
            .then(markProductsAsDirty);
    }

    async function remove(id) {
        const index = getItemIndex(id);
        if (index !== -1) {
            items.value.splice(index, 1);
            const productIndex = getProductIndex(id);
            if (productIndex !== -1) {
                products.value.splice(productIndex, 1);
            }
        }
        await api.destroy(id, key.value);
    }

    async function removeDebounced(id, callback) {
        const ids = Array.isArray(id) ? id : [id];

        ids.forEach(singleId => {
            const index = getItemIndex(singleId);
            if (index !== -1) {
                items.value.splice(index, 1);
                const productIndex = getProductIndex(singleId);
                if (productIndex !== -1) {
                    products.value.splice(productIndex, 1);
                }
            }
        });

        ids.forEach(singleId => {
            if (!pendingRemovalIds.value.includes(singleId)) {
                pendingRemovalIds.value.push(singleId);
            }
        });

        if (removalTimeout) {
            clearTimeout(removalTimeout);
        }
        removalTimeout = setTimeout(async () => {
            const idsToRemove = [...pendingRemovalIds.value];
            pendingRemovalIds.value = [];
            removalTimeout = null;
            await api.multipleDestroy(idsToRemove, key.value);

            if (callback) {
                callback();
            }
        }, 300);
    }

    function markProductsAsDirty(response) {
        productsDirty.value = true;
        useCheckoutStore().refreshCheckoutKey();

        return response;
    }

    function hasProduct(id) {
        return getProductQuantity(id) > 0;
    }

    function getItemIndex(id) {
        return items.value.findIndex((item) => item.id === id);
    }

    function getProductIndex(id) {
        return products.value.findIndex((product) => product.id === id);
    }

    function getProductQuantity(product_id) {
        const item = items.value.find((item) => item.id === product_id);
        return item ? item.quantity : 0;
    }

    function storeUpdate(id, quantity) {
        const index = getItemIndex(id);
        if (index !== -1) {
            items.value[index].quantity = quantity;
        } else {
            items.value.push({ id, quantity });
        }
    }

    async function storeUpdateSave(id, quantity, silent = true, step = 1) {
        quantity = QuantityStepMixin.methods.adjustQuantity(quantity, step);
        let response = null;
        if (hasProduct(id)) {
            if (quantity > 0) {
                response = await updateCart(id, quantity, silent);
            } else {
                remove(id);
            }
        } else {
            response = await addToCart(id, quantity, silent);
        }
        if (!silent && response) {
            const routeName = useRoute().name?.split('_')[0] || '';
            if (routeName !== 'checkout') {
                useModal().openModal('ProductAddedToCart', ProductAddedToCart, response.data);
            }
        }
    }

    async function fetchCheckoutData() {
        if (!productsDirty.value) return;
        const checkoutStore = useCheckoutStore();
        const url = 'checkout?cart-key=' + key.value;
        const response = await getFromServer(url, { key: checkoutStore.checkoutKey + url });
        products.value = response.products;
        withVat.value = response.with_vat;
        checkoutErrors.value = response.errors;
        productsDirty.value = false;
        useCheckoutStore().setBundleData(response);
    }

    function clear() {
        if (items.value.length > 0) {
            const ids = items.value.map(item => item.id);
            api.multipleDestroy(ids, key.value);
        }

        items.value = [];
        products.value = [];
        productsDirty.value = true;
        checkoutErrors.value = [];
    }

    return {
        items,
        products,
        productsDirty,
        withVat,
        initData,
        fetchCart,
        fetchCheckoutData,
        addToCart,
        addToCartSilent,
        updateCart,
        remove,
        removeDebounced,
        storeUpdateSave,
        hasProduct,
        getProductQuantity,
        count,
        setKey,
        defaultKey,
        key,
        checkoutErrors,
        anonymous,
        markProductsAsDirty,
        clear,
    };
});
